<template>
  <article class="rating-card">
    <div class="rating-card__aside">
      <div
        class="rating-card__position"
        v-text="`#${rating.position}`"
      />
    </div>
    <div class="rating-card__main">
      <div class="rating-card__company">
        <div class="rating-card__company-aside">
          <BasicAvatar
            :href="rating.company.href"
            :image="rating.company.avatar"
            size="medium"
            roundness="small"
            target="_blank"
          />
        </div>
        <div class="rating-card__company-main">
          <div class="rating-card__company-first-line">
            <span class="rating-card__company-title">
              <BasicText size="large">
                <BasicLink
                  :href="rating.company.href"
                  appearance="dark-bold"
                  target="_blank"
                >
                  <h2
                    class="rating-card__company-title-text"
                    v-text="rating.company.title"
                  />
                </BasicLink>
              </BasicText>
            </span>
            <CompanyIcons :company="rating.company" />
            <span
              v-if="rating.summary.visible_summary"
              class="rating-card__company-rating"
            >
              <CompanyRating :rating="rating.summary" />
            </span>
            <BasicButton
              v-if="rating.company.rateHref"
              appearance="rate"
              :href="rating.company.rateHref"
              size="m"
              target="_blank"
            >
              Оценить
            </BasicButton>
          </div>
          <div
            v-if="rating.company.description"
            class="rating-card__company-description"
            v-text="rating.company.description"
          />
          <div
            v-if="meta.length !== 0"
            class="rating-card__company-meta"
          >
            <InlineList
              :items="meta"
              v-slot="{ item }"
            >
              <LinkOrText
                :item="item"
                :appearance="item.appearance"
              />
            </InlineList>
          </div>
          <div
            v-if="rating.company.awards"
            class="rating-card__company-awards"
          >
            <AwardImageList :awards="rating.company.awards" />
          </div>
        </div>
      </div>
      <div class="rating-card__scores">
        <div class="rating-card__scores-toggle">
          <BasicButton @click="scoresVisible = !scoresVisible">
            <BasicText
              color="emphasized"
              weight="bold"
            >
              <span class="rating-card__scores-toggle-wrapper">
                <span
                  class="rating-card__scores-value"
                  v-text="rating.scores.featured.value"
                />
                <span class="rating-card__scores-title">
                  <InlineAlign gap="small">
                    <template #default>
                      <span v-text="rating.scores.featured.title" />
                    </template>
                    <template #end>
                      <ArrowIcon
                        direction="bottom"
                        color="emphasized"
                        size="18"
                      />
                    </template>
                  </InlineAlign>
                </span>
              </span>
            </BasicText>
          </BasicButton>
        </div>
        <TransitionExpand>
          <div
            v-if="scoresVisible"
            class="rating-card__score-list"
          >
            <ScoreList :scores="rating.scores.items" />
          </div>
        </TransitionExpand>
      </div>
      <div
        v-if="rating.review"
        class="rating-card__review"
      >
        <TransitionExpand>
          <div
            :key="reviewCollapsed"
            class="rating-card__review-wrapper"
            :class="bem('rating-card__review-wrapper', { collapsed: reviewCollapsed })"
          >
            <section
              v-if="rating.review.summary"
              class="rating-card__review-message"
            >
              <StyleUgc :ugc="rating.review.summary" />
            </section>
            <template v-if="rating.review.positives">
              <h3 class="rating-card__review-section-title">
                Достоинства
              </h3>
              <section class="rating-card__review-message">
                <StyleUgc :ugc="rating.review.positives" />
              </section>
            </template>
            <template v-if="rating.review.negatives">
              <h3 class="rating-card__review-section-title">
                Недостатки
              </h3>
              <section class="rating-card__review-message">
                <StyleUgc :ugc="rating.review.negatives" />
              </section>
            </template>
          </div>
        </TransitionExpand>
        <div
          v-if="rating.review.shouldCollapse"
          class="rating-card__review-collapse-button"
          :class="bem('rating-card__review-collapse-button', { collapsed: reviewCollapsed })"
        >
          <BasicButton
            appearance="pseudo-link"
            size="m"
            @click="onCollapseButtonClick"
          >
            {{ collapseButtonTitle }}
          </BasicButton>
        </div>
      </div>
    </div>
  </article>
</template>

<script>


  import { bem } from 'src/utils/bem.js';

  import AwardImageList from 'src/components/awards/AwardImageList/AwardImageList.vue';
  import BasicAvatar from 'src/components/BasicAvatar/BasicAvatar.vue';
  import BasicButton from 'src/components/BasicButton/BasicButton.vue';
  import BasicLink from 'src/components/BasicLink/BasicLink.vue';
  import ArrowIcon from 'src/components/icons/ArrowIcon/ArrowIcon.vue';
  import InlineAlign from 'src/components/InlineAlign/InlineAlign.vue';
  import InlineList from 'src/components/InlineList/InlineList.vue';
  import LinkOrText from 'src/components/LinkOrText/LinkOrText.vue';
  import ScoreList from 'src/components/ratings/ScoreList/ScoreList.vue';
  import CompanyRating from 'src/components/resume/CompanyRating/CompanyRating.vue';
  import StyleUgc from 'src/components/StyleUgc/StyleUgc.vue';
  import BasicText from 'src/components/text/BasicText/BasicText.vue';
  import TransitionExpand from 'src/components/transitions/TransitionExpand/TransitionExpand.vue';
  import CompanyIcons from '../../companies/CompanyIcons/CompanyIcons.vue';
  
  export default {
    name: 'RatingCard',
    components: {
      CompanyIcons,
      AwardImageList,
      ScoreList,
      LinkOrText,
      CompanyRating,
      BasicButton,
      InlineAlign,
      BasicText,
      ArrowIcon,
      TransitionExpand,
      BasicLink,
      InlineList,
      BasicAvatar,
      StyleUgc,
    },
    props: {
      rating: {
        type: Object,
        required: true,
      },
    },
    data() {
      const { review = {} } = this.rating;
      const { shouldCollapse } = review;
      return {
        scoresVisible: false,
        reviewCollapsed: Boolean(shouldCollapse),
      };
    },
    methods: {
      bem,
      onCollapseButtonClick(event) {
        this.reviewCollapsed = !this.reviewCollapsed;
        event.target.blur();
      },
    },
    computed: {
      meta() {
        const { location, vacancies } = this.rating.company;
        const res = [];
        if (location) res.push({ ...location, appearance: 'dark' });
        if (vacancies) res.push({ ...vacancies, appearance: null });
        return res;
      },
      collapseButtonTitle() {
        if (this.reviewCollapsed) return 'Читать полностью';
        return 'Скрыть';
      },
    },
  };
</script>